import { i18n } from '@/plugins/i18n';
import VitalityCriterion from '@/utils/classes/VitalityCategory/VitalityCriterion';

// Score breakdown
// Score if the problem has verified causes (more than half of causes are verified)
const vccicVerifiedCauses = 80;
// Score if at least one cause is from Ishikawa or 5 whys
const vccicIshikawaOrFiveWhys = 20;
// Score per action if linked to a cause
const vccicActionLinked = 30;
// Score per action if linked to a true cause
const vccicActionLinkedTrue = 70;

/**
 * Score base on how problems are analysed and resolved.
 * Teams must determine root causes of problems before working on them.
 * This score will go down if the completed problems don't have verified causes
 * and if actions are carried out to solve false causes.
 *
 * • Problems without verified causes
 *     + Problem has verified causes
 *     + At least one cause is from 5W or Ishikawa
 * • Actions not on true causes
 * • Optional : True causes without actions (as of August 3rd, it's not possible to specify
 *     multiple causes for an action so this shouldn't lower the score)
 *
 * See score breakdown above.
 */
export default new VitalityCriterion({
  titleTr: 'VH_PB_CAUSES_IN_COMPLETED',
  key: 'completedProblemsCauses',
  weight: 1.7,

  score: ({ problems }, { start, end }) => {
    const pPbs = problems.filter(p =>
      p.state === 4 &&
      start.isBefore(p.lastActionAct) &&
      end.isAfter(p.lastActionAct)
    );

    const nProblems = pPbs.length;
    let nPbWithVerified = 0;
    let nPbWithIshiFiveWhys = 0;

    let nActions = 0;
    let nActionsLinked = 0;
    let nActionsLinkedToTrue = 0;
    for (let i = 0, len = nProblems; i < len; i++) {
      const p = pPbs[i];
      const nCauses = p.causes.length;
      const nVerifiedCauses = p.causes.filter(c => c.isVerified === 1).length;
      if (nCauses > 0 && (nVerifiedCauses >= nCauses / 2 || nVerifiedCauses > 2)) {
        nPbWithVerified++;
        if (p.causes.some(c => c.type > 0)) {
          nPbWithIshiFiveWhys++;
        }
      }
      nActions += p.actions.length;
      if (p.actions.length > 0) {
        const causesPerId = p.causes.reduce((all, cause) => ({ ...all, [`${cause.id}`]: cause }), {});
        p.actions.forEach((action) => {
          if (causesPerId[`${action.cause}`]) {
            nActionsLinked++;
            if (causesPerId[`${action.cause}`].isReal) {
              nActionsLinkedToTrue++;
            }
          }
        });
      }
    }
    // Score of problems
    let problemsScore = 0;
    if (nProblems > 0) {
      problemsScore =
        (nPbWithVerified * vccicVerifiedCauses + nPbWithIshiFiveWhys * vccicIshikawaOrFiveWhys)
        / nProblems;
    }
    // Score of actions
    let actionsScore = 0;
    if (nActions > 0) {
      actionsScore =
        (nActionsLinked * vccicActionLinked + nActionsLinkedToTrue * vccicActionLinkedTrue)
        / nActions;
    }
    const score = problemsScore / 2 + actionsScore / 2;
    return {
      score,
      data: {
        nProblems,
        nActions,
        nPbWithVerified,
        nPbWithIshiFiveWhys,
        nActionsLinked,
        nActionsLinkedToTrue,
      },
    };
  },

  text({ nProblems, nActions, nPbWithVerified, nPbWithIshiFiveWhys, nActionsLinked, nActionsLinkedToTrue }) {
    if (nProblems === 0) {
      return i18n.t('message.VH_PB_TEXT_NONE_RELATED');
    }
    const stringParams = {};
    const multiplier = 100 / nProblems;
    stringParams.percVerified = (nPbWithVerified * multiplier).sToFixed(0);
    stringParams.percAnalysis = (nPbWithIshiFiveWhys * multiplier).sToFixed(0);
    const acMultiplier = 100 / nActions;
    stringParams.percLinked = (nActionsLinked * acMultiplier).sToFixed(0);
    stringParams.percLinkedTrue = (nActionsLinkedToTrue * acMultiplier).sToFixed(0);

    return i18n.t('message.VH_PB_CAUSES_IN_COMPLETED_TEXT', stringParams);
  },
});
